import { Button, Card, Flex, Tag, theme } from 'antd';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import styled from 'styled-components';

import { colorForKey } from '@utils/objectColor';
import ColorDot from '@components/modal/objectPickerModal/ColorDot';
import BaseText from '@components/typography/BaseText';

import { objectCountLabel } from '../collectionTree';

// The selectable card's control, styled to read as plain text. The name, not the
// card, is the button, so the Edit and Delete buttons beside it aren't nested in it.
const NAME_BUTTON_STYLES = {
	root: { flex: 1, minWidth: 0, height: 'auto', justifyContent: 'flex-start' },
	content: { minWidth: 0, overflow: 'hidden' },
};

// Drops the link button's own inset so the name lines up with the card's content.
const NameButton = styled(Button)`
	&& {
		padding: 0;
	}
`;

/**
 * One collection in the Collections page grid.
 *
 * - A template passes `onCopy` and gets a full-width copy button (`copyLabel`).
 * - An org or personal collection passes `onSelect`: its name is a toggle button,
 *   and a click anywhere on the card selects it too. `onEdit` / `onDelete` (when
 *   given) add the icon buttons, which don't select.
 *
 * @param {{ id, name, objects: Array<{ key, label, color? }> }} props.collection
 * @param {string} [props.copyLabel] - the copy button's text
 * @param {boolean} [props.copyDisabled] - the template can't be copied yet
 */
const CollectionGridCard = ({
	collection,
	selected = false,
	onSelect,
	onEdit,
	onDelete,
	onCopy,
	copyLabel = 'Copy to my collections',
	copying = false,
	copyDisabled = false,
}) => {
	const { token } = theme.useToken();

	const isSelectable = Boolean(onSelect);
	// A selectable card's resting border matches its fill, so only the selected one shows an edge.
	const restingBorder = isSelectable ? token.colorBgContainer : token.colorBorder;

	const handleAction = handler => event => {
		event.stopPropagation();
		handler(collection);
	};

	const name = (
		<BaseText size="sm" strong ellipsis={{ tooltip: collection.name }}>
			{collection.name}
		</BaseText>
	);

	return (
		<Card
			size="small"
			// A mouse shortcut only: the name button is the keyboard and screen-reader control.
			onClick={isSelectable ? () => onSelect(collection) : undefined}
			style={{
				height: '100%',
				cursor: isSelectable ? 'pointer' : undefined,
				borderColor: selected ? token.colorPrimary : restingBorder,
			}}
			styles={{ body: { height: '100%' } }}>
			<Flex vertical gap="middle" style={{ height: '100%' }}>
				<Flex align="center" gap="small">
					{isSelectable ? (
						<NameButton
							color="default"
							variant="link"
							aria-pressed={selected}
							aria-label={`Select ${collection.name}`}
							onClick={handleAction(onSelect)}
							styles={NAME_BUTTON_STYLES}>
							{name}
						</NameButton>
					) : (
						<div style={{ flex: 1, minWidth: 0 }}>{name}</div>
					)}
					<BaseText size="xs" type="secondary" style={{ flex: 'none' }}>
						{objectCountLabel(collection.objects.length)}
					</BaseText>
					{onEdit && (
						<Button
							size="small"
							icon={<FiEdit2 />}
							aria-label={`Edit ${collection.name}`}
							onClick={handleAction(onEdit)}
						/>
					)}
					{onDelete && (
						<Button
							size="small"
							danger
							icon={<FiTrash2 />}
							aria-label={`Delete ${collection.name}`}
							onClick={handleAction(onDelete)}
						/>
					)}
				</Flex>

				<Flex gap="small" wrap>
					{collection.objects.map(object => (
						<Tag
							key={object.key}
							icon={
								<ColorDot
									color={object.color ?? colorForKey(object.key)}
									aria-hidden
								/>
							}
							// antd spaces only its own `.anticon` icons from the label, and
							// replaces the icon's own `style` with this one.
							styles={{ icon: { marginInlineEnd: token.marginXS } }}
							style={{ margin: 0 }}>
							<BaseText size="xs">{object.label}</BaseText>
						</Tag>
					))}
				</Flex>

				{onCopy && (
					<Button
						block
						loading={copying}
						disabled={copyDisabled}
						onClick={handleAction(onCopy)}
						style={{ marginTop: 'auto' }}>
						{copyLabel}
					</Button>
				)}
			</Flex>
		</Card>
	);
};

export default CollectionGridCard;
